import { useState } from 'react';
import axios from 'axios';
import { ArrowRight, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service_type: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success(t('contact.form.success'));
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t('contact.form.error'));
    } finally {
      setLoading(false);
    }
  };

  const serviceOptions = [
    { value: 'port_agency', label: language === 'en' ? 'Port Agency' : 'Портовое агентство' },
    { value: 'cruise_agency', label: language === 'en' ? 'Cruise Agency' : 'Круизное агентство' },
    { value: 'yacht_agency', label: language === 'en' ? 'Yacht Agency' : 'Яхтенное агентство' },
    { value: 'ship_supply', label: language === 'en' ? 'Ship Supply' : 'Судовое снабжение' },
  ];

  if (submitted) {
    return (
      <main data-testid="contact-page" className="min-h-screen pt-24">
        <section className="py-32 lg:py-48 bg-slate-50">
          <div className="container-bespoke text-center">
            <p className="text-editorial text-signal mb-6">
              {language === 'en' ? 'Message Sent' : 'Сообщение отправлено'}
            </p>
            <h1 className="font-heading text-5xl lg:text-6xl font-extrabold text-navy mb-8">
              {language === 'en' ? 'Thank you' : 'Спасибо'}
            </h1>
            <p className="text-xl text-slate-600 mb-12 max-w-md mx-auto">
              {language === 'en'
                ? 'We\'ll get back to you within 24 hours.'
                : 'Мы свяжемся с вами в течение 24 часов.'}
            </p>
            <button onClick={() => setSubmitted(false)} className="btn-outline">
              {language === 'en' ? 'Send Another' : 'Отправить еще'}
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="contact-page" className="min-h-screen pt-24">
      {/* Header with Video Background */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        {/* Video Background - Ship Bridge with Communication */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="https://images.pexels.com/videos/9655470/above-sea-boat-bridge-of-ship-bridge-ship-9655470.jpeg?auto=compress&cs=tinysrgb&w=1920"
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src="https://videos.pexels.com/video-files/9655470/9655470-uhd_2560_1440_24fps.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-navy/60 z-10" />
        
        <div className="container-bespoke relative z-20">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-editorial text-white/70 mb-6">
                {language === 'en' ? 'Get in Touch' : 'Свяжитесь'}
              </p>
              <h1
                data-testid="contact-title"
                className="font-heading text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-8 drop-shadow-lg"
              >
                {t('contact.title')}
              </h1>
              <p className="text-xl text-white/90">
                {language === 'en'
                  ? 'Request a quote or ask us anything about our services.'
                  : 'Запросите цену или задайте любой вопрос о наших услугах.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-bespoke">
          <div className="grid lg:grid-cols-12 gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              {/* Company Info - Moved to top */}
              <div className="mb-12 pb-8 border-b border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="bg-navy/5 p-3 rounded-lg">
                    <svg className="w-6 h-6 text-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-heading font-bold text-navy text-lg mb-2">
                      {language === 'en' ? 'Company Registration' : 'Регистрация компании'}
                    </p>
                    <p className="text-slate-700 font-medium mb-4">
                      Rota Denizcilik Turizm ve Dış Ticaret A.Ş.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="bg-slate-50 p-3 rounded">
                        <span className="text-slate-500">MERSIS No:</span>
                        <span className="text-navy font-mono ml-1">0735002496900016</span>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <span className="text-slate-500">Tax ID (Sinop):</span>
                        <span className="text-navy font-mono ml-1">7350024969</span>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <span className="text-slate-500">Trade Registry No:</span>
                        <span className="text-navy font-mono ml-1">760-Sinop</span>
                      </div>
                      <div className="bg-slate-50 p-3 rounded">
                        <span className="text-slate-500">Ship Agency License:</span>
                        <span className="text-navy font-mono ml-1">0466</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <form
                data-testid="contact-form"
                onSubmit={handleSubmit}
                className="space-y-8"
              >
                <div className="grid sm:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-editorial text-navy">
                      {t('contact.form.name')} *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-slate-50 border-slate-200 focus:border-signal h-14 text-navy"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-editorial text-navy">
                      {t('contact.form.email')} *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="input-email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-slate-50 border-slate-200 focus:border-signal h-14 text-navy"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-editorial text-navy">
                      {t('contact.form.company')}
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      data-testid="input-company"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-slate-50 border-slate-200 focus:border-signal h-14 text-navy"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-editorial text-navy">
                      {t('contact.form.phone')}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      data-testid="input-phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-slate-50 border-slate-200 focus:border-signal h-14 text-navy"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-editorial text-navy">
                    {t('contact.form.service')} *
                  </Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, service_type: v }))}
                    required
                  >
                    <SelectTrigger
                      data-testid="select-service"
                      className="bg-slate-50 border-slate-200 focus:border-signal h-14 text-navy"
                    >
                      <SelectValue placeholder={language === 'en' ? 'Select a service' : 'Выберите услугу'} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {serviceOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="text-navy hover:bg-slate-50"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-editorial text-navy">
                    {t('contact.form.message')} *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="input-message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="bg-slate-50 border-slate-200 focus:border-signal text-navy resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="submit-button"
                  disabled={loading}
                  className="btn-signal"
                >
                  {loading ? t('contact.form.sending') : t('contact.form.submit')}
                  <ArrowRight className="inline ml-2 w-4 h-4" />
                </button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-5">
              <div className="bg-navy p-8 lg:p-12 text-white">
                {/* Head Office */}
                <div className="mb-10">
                  <p className="text-editorial text-signal mb-3">Head Office</p>
                  <p className="text-white leading-relaxed">
                    Meydankapi Mah. Denizcileryolu<br />
                    Rota Business Center No:1/1-2<br />
                    <span className="text-slate-400">Sinop / Turkey</span>
                  </p>
                </div>

                {/* Branch Office Istanbul */}
                <div className="mb-10 pt-8 border-t border-white/10">
                  <p className="text-editorial text-signal mb-3">Branch Office / İstanbul</p>
                  <p className="text-white leading-relaxed">
                    Kanuni Cad. Nef Sancaktepe<br />
                    A2 Blok Daire 6<br />
                    <span className="text-slate-400">Istanbul / Turkey</span>
                  </p>
                </div>

                {/* Branch Office Amasra */}
                <div className="mb-10 pt-8 border-t border-white/10">
                  <p className="text-editorial text-signal mb-3">Branch Office / Amasra</p>
                  <p className="text-white leading-relaxed">
                    Kaleşah Mah. Vadi Evleri Sitesi No:25<br />
                    <span className="text-slate-400">Amasra / Bartın, Turkey</span>
                  </p>
                </div>

                {/* Contact Info */}
                <div className="pt-8 border-t border-white/10">
                  <p className="text-editorial text-signal mb-4">
                    {language === 'en' ? 'Contact' : 'Контакт'}
                  </p>
                  <div className="space-y-3">
                    <a href="tel:+903682618874" className="font-mono text-white hover:text-signal transition-colors block">+90 368 261 88 74</a>
                    <div>
                      <a href="tel:+905370618874" className="font-mono text-white hover:text-signal transition-colors block">+90 537 061 88 74</a>
                      <p className="text-sm text-slate-400">{language === 'en' ? '24/7 After Hours' : '24/7 Внерабочее время'}</p>
                    </div>
                    <a href="mailto:info@rotaship.com" className="font-mono text-slate-400 hover:text-white transition-colors block">info@rotaship.com</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section
        data-testid="google-map"
        className="h-[400px] relative overflow-hidden"
      >
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d747.2!2d35.149505!3d42.024786!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x408f12c289f09df5%3A0xe10fb57ba2d0d55e!2sRota%20Maritime%20Agency!5e0!3m2!1sen!2str!4v1700000000000"
          width="100%"
          height="100%"
          style={{ 
            border: 0,
            filter: 'grayscale(30%) sepia(30%) saturate(120%) hue-rotate(180deg) brightness(90%) contrast(95%)'
          }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Rota Maritime Agency Location - Sinop, Turkey"
        />
      </section>
    </main>
  );
};
